/**
 * "Saímos em 20 min" — o megafone de quem administra. Chips prontos e um
 * texto livre; vai por push e pelo sino. E o card do último aviso, para
 * todo mundo, enquanto vale.
 */
import { uuidv7 } from '@nv/core';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { usePermissao, useTrip } from '../../trip/TripContext';
import { Icon } from '../../ui/icons';
import { Sheet } from '../../ui/Sheet';
import { Row } from '../../ui/primitives';
import { Card } from '../../ui/primitives';
import { type Aviso, encontroApi, useInvalidarEncontro } from './api';

const PRONTOS = ['Saímos em 20 min', 'Saímos em 10 min', 'Estamos atrasados', 'Hora do almoço'];

export function AvisoAtual({ aviso }: { aviso: Aviso }) {
  const d = new Date(aviso.createdAt);
  return (
    <Card className="aviso" data-testid="aviso">
      <span className="aviso__icone" aria-hidden="true"><Icon name="notificacoes" size={18} /></span>
      <span className="mapa__pessoa-texto">
        <span className="enc__titulo">{aviso.text}</span>
        <span className="mapa__pessoa-familia">{aviso.createdBy.name} · <span className="mono">{String(d.getHours()).padStart(2, '0')}:{String(d.getMinutes()).padStart(2, '0')}</span></span>
      </span>
    </Card>
  );
}

/**
 * Sem botão visível: só a folha, aberta por `/?aviso=1` (o "+" da barra). É o
 * caminho da Home desde o lote de aparência 2, quando os atalhos saíram dela.
 */
export function AvisoRapidoPeloMais() {
  return <BotaoAvisoRapido comoLinha={false} soFolha />;
}

export function BotaoAvisoRapido({ comoLinha = false, soFolha = false }: { comoLinha?: boolean; soFolha?: boolean } = {}) {
  const { tripId } = useTrip();
  const pode = usePermissao('manage_trip_settings');
  const avisar = useInvalidarEncontro(tripId);
  // `/?aviso=1` chega do "+" da barra (lote de aparência 1): abre direto.
  const [busca, setBusca] = useSearchParams();
  const [aberto, setAberto] = useState(busca.get('aviso') === '1');
  // Quem já está na Home e toca o "+" só muda a query: abre também nesse caso,
  // e limpa a query para o aviso não reabrir num recarregamento.
  useEffect(() => {
    if (busca.get('aviso') === '1') {
      setAberto(true);
      setBusca({}, { replace: true });
    }
  }, [busca, setBusca]);
  const [texto, setTexto] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  if (!pode) return null;

  async function enviar(t: string) {
    setEnviando(true);
    setErro(null);
    try {
      await encontroApi.avisar(tripId, { id: uuidv7(), text: t });
      avisar();
      setAberto(false);
      setTexto('');
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível avisar agora.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <>
      {soFolha ? null : comoLinha ? (
        <Row icon="notificacoes" title="Alertar o grupo" onClick={() => setAberto(true)} data-testid="aviso-rapido" />
      ) : (
        <button type="button" className="nv-btn nv-btn--secondary nv-btn--block" onClick={() => setAberto(true)} data-testid="aviso-rapido">
          <Icon name="notificacoes" size={18} />
          Alertar o grupo
        </button>
      )}
      {aberto && (
        <Sheet titleId="aviso-titulo" onFechar={() => setAberto(false)}>
            <h2 className="sheet__title" id="aviso-titulo">Alertar o grupo</h2>
            <p className="sheet__text">Vai por notificação para todo mundo que ligou alertas, e fica no sino de todos.</p>
            <div className="chip-row">
              {PRONTOS.map((t) => (
                <button type="button" key={t} className="pick" disabled={enviando} onClick={() => void enviar(t)}>
                  {t}
                </button>
              ))}
            </div>
            <label className="field">
              <span className="field__label">Ou escreva</span>
              <input className="field__input" value={texto} onChange={(e) => setTexto(e.target.value)} maxLength={140} aria-label="Aviso" />
            </label>
            {erro && <p className="auth-error" role="alert">{erro}</p>}
            <div className="fin__ctas">
              <button type="button" className="nv-btn nv-btn--secondary" onClick={() => setAberto(false)}>Cancelar</button>
              <button type="button" className="nv-btn nv-btn--primary" disabled={!texto.trim() || enviando} onClick={() => void enviar(texto.trim())}>
                Enviar
              </button>
            </div>
        </Sheet>
      )}
    </>
  );
}
